import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../../hooks/useDashTheme";
import { CATS, useCatColors, fmtDur } from "./bits";

/* Time-allocation donut.

   Deliberately capped at THREE coloured slices plus a neutral "Other". Six
   categorical hues cannot be told apart in a form where every pair touches
   every other (a donut has no fixed adjacency), so the top three take the
   three hues that validate clean on the all-pairs list in both modes and the
   remainder folds. The full seven-way breakdown lives in the ranked list
   beside this chart, where direct labels carry identity.

   Every slice is directly labelled with its share, so the reading never
   depends on colour, and a 2px surface-coloured gap separates neighbours. */

const R_OUT = 78;
const R_IN = 52;
const CX = 96;
const CY = 96;

function arc(cx, cy, rOut, rIn, a0, a1) {
  const p = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const big = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = p(rOut, a0), [x1, y1] = p(rOut, a1);
  const [x2, y2] = p(rIn, a1), [x3, y3] = p(rIn, a0);
  return `M${x0},${y0} A${rOut},${rOut} 0 ${big} 1 ${x1},${y1} L${x2},${y2} A${rIn},${rIn} 0 ${big} 0 ${x3},${y3} Z`;
}

export function Donut({ apps, totals }) {
  const t = useDashTheme();
  const C = useCatColors();
  const [hover, setHover] = React.useState(null);

  /* Roll applications up to categories, then fold past third place.

     When every application lands in ONE category the category view says
     nothing ("Researching 100%"), so the chart drops a level and breaks the
     time down by application instead. Same three validated hues, same fold. */
  const { slices, basis } = React.useMemo(() => {
    const byCat = {};
    for (const a of apps || []) {
      if (a.cat === "idle") continue;
      byCat[a.cat] = (byCat[a.cat] || 0) + a.minutes;
    }
    const cats = Object.entries(byCat).sort((a, b) => b[1] - a[1]);

    const useApps = cats.length < 2;
    const ranked = useApps
      ? (apps || []).filter((a) => a.cat !== "idle").map((a) => [a.name, a.minutes]).sort((a, b) => b[1] - a[1])
      : cats;

    const head = ranked.slice(0, 3);
    const tail = ranked.slice(3);
    const out = head.map(([key, minutes], i) => ({
      key,
      label: useApps ? key : (CATS[key]?.label || key),
      minutes,
      // Rank picks the hue slot, and the slot list is the validated trio.
      color: [C.deep, C.browse, C.comms][i],
    }));
    if (tail.length) {
      out.push({
        key: "__other",
        label: tail.length === 1 ? (useApps ? tail[0][0] : CATS[tail[0][0]]?.label || "Other") : "Other",
        minutes: tail.reduce((a, r) => a + r[1], 0),
        color: C.idle,
        folded: tail.length > 1 ? tail.map(([k]) => (useApps ? k : CATS[k]?.label || k)) : null,
      });
    }
    return { slices: out, basis: useApps ? "application" : "category" };
  }, [apps, C]);

  const total = slices.reduce((a, s) => a + s.minutes, 0);
  if (!total) return null;

  // 2px surface gap between neighbours, expressed as an angular pad.
  const PAD = slices.length > 1 ? 0.028 : 0;
  let cursor = -Math.PI / 2;
  const geo = slices.map((s) => {
    const sweep = (s.minutes / total) * Math.PI * 2;
    const a0 = cursor + PAD / 2;
    const a1 = cursor + sweep - PAD / 2;
    cursor += sweep;
    return { ...s, a0: a1 > a0 ? a0 : cursor - sweep, a1: a1 > a0 ? a1 : cursor, mid: (a0 + a1) / 2, share: Math.round((s.minutes / total) * 100) };
  });

  const focus = hover != null ? geo[hover] : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
      <div style={{ position: "relative", flex: "none" }}>
        <svg width="192" height="192" viewBox="0 0 192 192" role="img"
             aria-label={`Time allocation: ${geo.map((g) => `${g.label} ${g.share} percent`).join(", ")}`}>
          {geo.map((g, i) => {
            /* A slice covering the whole circle cannot be an arc: its start and
               end points coincide, so the path collapses to nothing and the
               chart renders empty. A single-category day is exactly that case,
               so it draws as a stroked ring instead. */
            const isFull = g.a1 - g.a0 >= Math.PI * 2 - 0.001;
            const common = {
              fill: isFull ? "none" : g.color,
              initial: { opacity: 0, scale: 0.86 },
              animate: { opacity: hover == null || hover === i ? 1 : 0.42, scale: 1 },
              transition: { duration: 0.45, ease: t.motion.ease.out, delay: 0.1 + i * 0.09 },
              style: { transformOrigin: `${CX}px ${CY}px`, cursor: "pointer" },
              onMouseEnter: () => setHover(i),
              onMouseLeave: () => setHover(null),
            };
            return isFull ? (
              <motion.circle
                key={g.key} cx={CX} cy={CY} r={(R_OUT + R_IN) / 2}
                stroke={g.color} strokeWidth={R_OUT - R_IN} {...common}
              />
            ) : (
              <motion.path key={g.key} d={arc(CX, CY, R_OUT, R_IN, g.a0, g.a1)} {...common} />
            );
          })}
          {/* centre hero: the number the chart exists to deliver */}
          <text x={CX} y={CY - 4} textAnchor="middle"
                style={{ fontSize: 24, fontWeight: 600, fill: t.c.text, letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums" }}>
            {focus ? `${focus.share}%` : fmtDur(totals?.trackedMin || total)}
          </text>
          <text x={CX} y={CY + 14} textAnchor="middle"
                style={{ fontSize: 11, fill: t.c.textTertiary }}>
            {focus ? focus.label : "tracked"}
          </text>
        </svg>
      </div>

      {/* legend — always present, and each row states its own share so the
          chart is readable without colour */}
      <div style={{ flex: 1, minWidth: 170 }}>
        {basis === "application" && (
          <div style={{ fontSize: 12, color: t.c.textTertiary, marginBottom: 6 }}>
            All time fell in one category, so this splits by application
          </div>
        )}
        {geo.map((g, i) => (
          <motion.div
            key={g.key}
            initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: t.motion.ease.out, delay: 0.2 + i * 0.07 }}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            style={{
              display: "flex", alignItems: "center", gap: 9, padding: "6px 0",
              borderTop: i ? `1px solid ${t.c.border}` : "none", cursor: "default",
            }}
          >
            <i style={{ width: 10, height: 10, borderRadius: 3, background: g.color, flex: "none" }} />
            <span style={{ flex: 1, fontSize: t.font.size.md, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {g.label}
              {g.folded && (
                <span style={{ color: t.c.textTertiary, fontSize: t.font.size.xs }}> · {g.folded.join(", ")}</span>
              )}
            </span>
            <span style={{ fontSize: t.font.size.md, fontWeight: t.font.weight.semibold, fontVariantNumeric: "tabular-nums" }}>
              {fmtDur(g.minutes)}
            </span>
            <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, minWidth: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {g.share}%
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
